/**
 * O que faz: painel "Resultados dos agentes" (últimos 30 dias): uso, acerto, resultado dos retornos e custo.
 * Onde usa: SecaoAgentes, abaixo da configuração.
 * Conexões: buscarMetricas('agentes') → RPC get_metricas_agentes_v1 (contrato RespostaMetricas validado por Zod).
 * Tabelas: indiretamente lead_classification_events, lead_stage_movements, lead_return_suggestions e ai_usage_events.
 * Data: 25/09/2026.
 */
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useAuthUser } from '@/hooks/useAuthUser';
import { buscarMetricas } from '@/services/metricasService';

interface ResultadosAgentesProps {
  clinicaId: string;
  modoAdmin: boolean;
}

// Indicadores mostrados, na ordem de leitura: uso → acerto → resultado → custo.
const DESTAQUES: Array<{ id: string; rotulo: string }> = [
  { id: 'leads_analisados', rotulo: 'Leads analisados' },
  { id: 'movimentos_ia', rotulo: 'Cards movidos pela IA' },
  { id: 'taxa_correcao_ia', rotulo: 'Movimentos corrigidos pela equipe' },
  { id: 'taxa_aceite_etapa', rotulo: 'Sugestões de etapa aceitas' },
  { id: 'retornos_sugeridos', rotulo: 'Retornos sugeridos' },
  { id: 'retornos_ia_com_resposta', rotulo: 'Retornos com resposta do lead' },
  { id: 'retornos_ia_com_agendamento', rotulo: 'Retornos que viraram agendamento' },
  { id: 'custo_ia_centavos', rotulo: 'Custo de IA' },
];

function dataCivil(deslocamentoDias: number): string {
  const instante = new Date(Date.now() + deslocamentoDias * 86_400_000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(instante);
}

function formatar(valor: number | null, unidade: string): string {
  if (valor === null) return 'Sem base';
  if (unidade === 'percentual') return `${valor.toLocaleString('pt-BR')}%`;
  if (unidade === 'centavos') return (valor / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  return valor.toLocaleString('pt-BR');
}

export function ResultadosAgentes({ clinicaId, modoAdmin }: ResultadosAgentesProps) {
  const { user } = useAuthUser();
  const periodo = { inicio: dataCivil(-29), fim: dataCivil(0) };
  const consulta = useQuery({
    queryKey: ['agentes', 'resultados', clinicaId, periodo.inicio, periodo.fim],
    enabled: Boolean(user?.id && clinicaId),
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => buscarMetricas('agentes', {
      usuarioId: user?.id as string, clinicaId, modo: modoAdmin ? 'admin' : 'clinica', versaoPermissoes: 'agentes',
      periodo, comparar: false, filtros: {},
    }, signal),
  });

  if (consulta.isLoading) {
    return <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Carregando resultados…</p>;
  }
  if (consulta.isError || !consulta.data) {
    return <p className="text-sm text-muted-foreground">Não foi possível carregar os resultados dos agentes agora.</p>;
  }
  const porId = new Map(consulta.data.indicadores.map((indicador) => [indicador.id, indicador]));

  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {DESTAQUES.map(({ id, rotulo }, indice) => {
          const indicador = porId.get(id);
          const valor = indicador?.valor ?? null;
          return (
            // Os números entram em cascata curta depois do carregamento.
            <div key={id} style={{ animationDelay: `${indice * 35}ms` }} className="rounded-xl border border-border p-3 animate-in fade-in-0 slide-in-from-bottom-1 fill-mode-both duration-300 motion-reduce:animate-none">
              <dt className="text-xs text-muted-foreground">{rotulo}</dt>
              <dd className="mt-1 text-lg font-semibold text-foreground">{indicador ? formatar(typeof valor === 'number' ? valor : valor === null ? null : Number(valor), indicador.unidade) : 'Sem base'}</dd>
              {indicador?.denominador != null && indicador.unidade === 'percentual' && (
                <dd className="text-[11px] text-muted-foreground">{indicador.numerador ?? 0} de {indicador.denominador}</dd>
              )}
            </div>
          );
        })}
      </dl>
      <p className="text-xs text-muted-foreground">Últimos 30 dias. Dados dos agentes existem a partir de 25/09/2026; resultado de retorno é associação em até 30 dias, não prova de causa.</p>
    </div>
  );
}
